if (!customElements.get('site-header')) {
  class SiteHeader extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.lastScrollY = window.scrollY;
      this.scrolling = false;
      this.submenuOpen = false;
      this.header = this.closest('header');
      this.headerOverlay = this.querySelector('[data-header-overlay]');
      this.submenuToggles = this.querySelectorAll('[data-submenu-toggle]');
      this.submenus = this.querySelectorAll('[data-submenu]');
      this.mobileNav = this.querySelector('[data-mobile-nav]');
      this.mobileNavToggle = this.querySelectorAll('[data-mobile-nav-toggle]');
      this.mobileAccountToggle = this.querySelector('[data-mobile-account-toggle]');
      this.mobileNavBackButtons = this.querySelectorAll('[data-mobile-nav-back]');
      this.announcementBanner = document.querySelector('announcement-banner');

      if (!this.header) return;

      window.addEventListener('scroll', () => this.handleScroll());

      if (this.dataset.shouldShow === 'true' && this.dataset.userStatus === 'logged-in') {
        this.handleLoggedInStateParam();
        this.handleLoggedInOptimizelyAudience();
      }

      document.addEventListener('click', (event) => {
        if (this.submenuOpen && !event.target.closest('site-header')) {
          this.closeSubmenus();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;

        const mobileNavActive = this.mobileNav && this.mobileNav.classList.contains('active');

        if (this.submenuOpen) {
          const activeToggle = Array.from(this.submenuToggles).find((toggle) => toggle.classList.contains('active'));
          this.closeSubmenus();

          if (mobileNavActive) {
            setTimeout(() => this._installCycleTrap([], this.mobileNav), 400);
          } else if (typeof window.removeTrapFocus === 'function') {
            window.removeTrapFocus(activeToggle);
          }
          return;
        }

        if (mobileNavActive) {
          this.toggleMobileNav();
          this.mobileNavToggle[0]?.focus();
        }
      });

      if (this.mobileAccountToggle) {
        this.mobileAccountToggle.addEventListener('click', () => {
          const mobileCustomerSubmenu = document.querySelector('[data-mobile-customer-submenu]');
          if (!mobileCustomerSubmenu) return;
          this.toggleMobileNav();
          mobileCustomerSubmenu.click();
        });
      }

      this.submenuToggles.forEach((submenuToggle) => submenuToggle.addEventListener('click', (event) => this.toggleSubmenu(event)));
      this.mobileNavToggle.forEach((mobileNavToggle) => mobileNavToggle.addEventListener('click', (event) => this.toggleMobileNav(event)));

      this.mobileNavBackButtons.forEach((mobileNavBackButton) => {
        mobileNavBackButton.addEventListener('click', () => {
          this.closeSubmenus();
          setTimeout(() => this._installCycleTrap(['.site-header__logo', '.site-header__mobile-nav-close'], this.mobileNav), 400);
        });
      });
    }
    handleLoggedInOptimizelyAudience() {
      window.optimizely = window.optimizely || [];
      window.optimizely.push({
        type: 'user',
        attributes: {
          has_logged_in: true,
        },
      });
    }
    handleLoggedInStateParam() {
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);
      if (!params.get('member')) {
        if (window.location.href.indexOf('#newsletter-signup') >= 0 || window.location.href.indexOf('#footer-newsletter') >= 0) return;
        params.append('member', 'true');
        window.location.href = `${url.pathname}?${params.toString()}`;
      }
    }

    // Install a Tab-cycle trap of [...header extras, ...visible focusables inside `container`].
    // Recomputed on every Tab so it reflects accordion/submenu visibility changes.
    _installCycleTrap(extrasSelectors, container) {
      this._removeCycleTrap();
      if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus();
      const getCycle = () => {
        const extras = extrasSelectors.map((sel) => this.header.querySelector(sel)).filter((el) => el && el.checkVisibility());
        const items = Array.from(container.querySelectorAll('a[href], button:enabled, summary')).filter((el) => el.checkVisibility());
        return { items, cycle: [...extras, ...items] };
      };
      const { items, cycle } = getCycle();
      if (!cycle.length) return;
      (items[0] || cycle[0]).focus();
      this._cycleTrap = (e) => {
        if (e.key !== 'Tab') return;
        const c = getCycle().cycle;
        const i = c.indexOf(document.activeElement);
        if (i < 0) return;
        e.preventDefault();
        c[(i + (e.shiftKey ? -1 : 1) + c.length) % c.length].focus();
      };
      document.addEventListener('keydown', this._cycleTrap);
    }

    _removeCycleTrap() {
      if (this._cycleTrap) {
        document.removeEventListener('keydown', this._cycleTrap);
        this._cycleTrap = null;
      }
    }

    toggleMobileNav() {
      if (!this.mobileNav) return;

      this.mobileNav.classList.toggle('active');
      this.header.classList.toggle('site-header-wrapper--mobile-nav-active');

      if (this.mobileNav.classList.contains('active')) {
        document.body.classList.add('overflow-hidden');
        setTimeout(() => this._installCycleTrap(['.site-header__logo', '.site-header__mobile-nav-close'], this.mobileNav), 400);
        this.calculateHeight();
      } else {
        this.closeSubmenus();
        document.body.classList.remove('overflow-hidden');
        this._removeCycleTrap();
        if (typeof window.removeTrapFocus === 'function') {
          window.removeTrapFocus();
        }
      }
    }

    calculateHeight() {
      if (this.announcementBanner) {
        if (window.scrollY > this.announcementBanner.offsetHeight) {
          this.mobileNav.style.height = `calc(100dvh - ${this.offsetHeight}px)`;
        } else {
          this.mobileNav.style.height = `calc(100dvh - ${this.offsetHeight + (this.announcementBanner.offsetHeight - window.scrollY)}px)`;
        }
      } else {
        this.mobileNav.style.height = `calc(100dvh - ${this.offsetHeight}px)`;
      }
    }

    setupSubmenuDisclosure(submenu) {
      const focusableSelector =
        "a[href], button:enabled, [tabindex]:not([tabindex^='-']), input:not([type=hidden]):enabled, select:enabled, textarea:enabled";
      const focusable = Array.from(submenu.querySelectorAll(focusableSelector)).filter((el) => el.checkVisibility());

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      setTimeout(() => first.focus(), 400);

      submenu.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;

        const tabbingForward = event.target === last && !event.shiftKey;
        const tabbingBackward = event.target === first && event.shiftKey;

        if (tabbingForward || tabbingBackward) {
          this.closeSubmenus();
        }
      });
    }

    closeSubmenus() {
      if (this.submenuOpen === false) return;

      this._removeCycleTrap();
      this.submenuToggles.forEach((submenuToggle) => {
        submenuToggle.classList.remove('active');
        submenuToggle.setAttribute('aria-expanded', false);
      });

      this.submenus.forEach((submenu) => {
        submenu.classList.remove('active');
        submenu.setAttribute('aria-hidden', true);
      });

      if (this.headerOverlay) {
        this.headerOverlay.classList.remove('active');
      }

      this.header.classList.remove('site-header-wrapper--mobile-nav-second-level-active');

      this.submenuOpen = false;
    }

    toggleSubmenu(event) {
      event.preventDefault();
      event.stopPropagation();

      const toggleEl = event.currentTarget;
      if (!toggleEl) return;

      if (toggleEl.classList.contains('active')) {
        this.closeSubmenus();
      } else {
        const targetSubmenuHandle = toggleEl.getAttribute('data-submenu-handle');

        if (!targetSubmenuHandle) return;

        if (toggleEl.hasAttribute('data-mobile-submenu-toggle')) {
          this.header.classList.add('site-header-wrapper--mobile-nav-second-level-active');
        }

        this.submenuToggles.forEach((submenuToggle) => {
          if (submenuToggle === toggleEl) {
            submenuToggle.classList.add('active');
            submenuToggle.setAttribute('aria-expanded', true);
          } else {
            submenuToggle.classList.remove('active');
            submenuToggle.setAttribute('aria-expanded', false);
          }
        });

        const isMobileSubmenu = toggleEl.hasAttribute('data-mobile-submenu-toggle');

        this.submenus.forEach((submenu) => {
          const submenuHandle = submenu.getAttribute('data-submenu-handle');

          if (submenuHandle === targetSubmenuHandle) {
            submenu.classList.add('active');
            submenu.setAttribute('aria-hidden', false);

            if (isMobileSubmenu) {
              setTimeout(() => this._installCycleTrap(['.site-header__mobile-nav-back', '.site-header__mobile-nav-close'], submenu), 400);
            } else {
              this.setupSubmenuDisclosure(submenu);
            }
          } else {
            submenu.classList.remove('active');
            submenu.setAttribute('aria-hidden', true);
          }
        });

        if (this.headerOverlay && window.innerWidth >= 1025) {
          this.headerOverlay.classList.add('active');
        }

        this.submenuOpen = true;
      }
    }

    handleScroll() {
      if (this.scrolling) return;

      window.requestAnimationFrame(() => {
        const headerHeight = this.header.offsetHeight;
        const scrollY = window.scrollY;

        if (window.innerWidth < 1025) {
          if (scrollY > this.lastScrollY) {
            // Scrolling down - hide header if past threshold
            if (scrollY >= headerHeight) {
              this.header.classList.add('site-header-wrapper--hidden');
            }
          } else {
            // Scrolling up - always show header
            this.header.classList.remove('site-header-wrapper--hidden');

            if (scrollY >= headerHeight) {
              this.header.classList.add('site-header-wrapper--fixed');
            }
          }

          if (scrollY === 0) {
            // At top - remove all scroll classes
            this.header.classList.remove('site-header-wrapper--fixed', 'site-header-wrapper--hidden');
          } else if (scrollY >= headerHeight && !this.header.classList.contains('site-header-wrapper--hidden')) {
            // Past header height and not hidden - should be fixed
            this.header.classList.add('site-header-wrapper--fixed');
          }
        } else {
          this.header.classList.add('site-header-wrapper--fixed');
        }

        if (scrollY === 0) {
          this.header.classList.remove('site-header-wrapper--fixed', 'site-header-wrapper--hidden');
        }

        this.lastScrollY = scrollY;
        this.scrolling = false;
      });

      this.scrolling = true;
    }
  }

  customElements.define('site-header', SiteHeader);
}
